import FormFieldsCard from "components/lib/Global/FormFieldsCard";
import _get from "lodash/get";
import { getTransformedFormFields } from "utils";

const getChangedFields = (
  official,
  latestVersion,
  viewRestrictedData,
  isCurrentUserRow
) => {
  const changedFields = [];

  // Find the official in latestVersion
  const latestOfficial = latestVersion?.find((o) => o._id === official._id);

  // If no latestOfficial, return empty (no changes to display)
  if (!latestOfficial) return changedFields;

  // Use official as the previous state (from previousVersion in CompanyOfficialSubRow)
  const previousOfficial = official;

  // Helper function to add field to changedFields if values differ
  const addToChangedFields = (
    name,
    key,
    currentValue,
    previousValue,
    isRestricted = false
  ) => {
    if (isRestricted && !(viewRestrictedData || isCurrentUserRow)) return;
    if (
      previousValue &&
      currentValue !== previousValue &&
      currentValue !== "N/A"
    ) {
      changedFields.push({
        name,
        key,
        value: currentValue, // Use current value from latestVersion
      });
    }
  };

  // Compare postalAddress fields
  if (latestOfficial.postalAddress && previousOfficial.postalAddress) {
    const postalAddressFields = getTransformedFormFields({
      idType: official.idType,
      formType: "postalAddress",
      formValues: latestOfficial.postalAddress,
    });

    postalAddressFields.forEach((field) => {
      const currentValue = field.value || "N/A";
      const previousValue = _get(
        previousOfficial.postalAddress,
        field.name,
        "N/A"
      );
      addToChangedFields(field.name, field.key, currentValue, previousValue);
    });
  }

  // Compare electronicAddress fields (restricted)
  if (latestOfficial.electronicAddress && previousOfficial.electronicAddress) {
    const currentEmail = _get(
      latestOfficial.electronicAddress,
      "emailAddress",
      "N/A"
    );
    const previousEmail = _get(
      previousOfficial.electronicAddress,
      "emailAddress",
      "N/A"
    );
    addToChangedFields(
      "emailAddress",
      "Email Address",
      currentEmail,
      previousEmail,
      true
    );

    const currentMobile = _get(
      latestOfficial.electronicAddress,
      "mobileNumber",
      "N/A"
    );
    const previousMobile = _get(
      previousOfficial.electronicAddress,
      "mobileNumber",
      "N/A"
    );
    addToChangedFields(
      "mobileNumber",
      "Mobile Number",
      currentMobile,
      previousMobile,
      true
    );
  }

  // Compare residentialAddressDetails fields
  if (
    latestOfficial.residentialAddressDetails &&
    previousOfficial.residentialAddressDetails
  ) {
    const residentialFields = [
      { key: "Building name", name: "buildingName" },
      { key: "House number", name: "houseNumber" },
      { key: "Estate", name: "estate" },
      { key: "Floor number", name: "floorNumber" },
      { key: "Street name", name: "streetName" },
      { key: "Street number", name: "streetNumber" },
      { key: "City", name: "city" },
      { key: "Country", name: "country" },
      { key: "County", name: "county" },
      { key: "District", name: "district" },
      { key: "Locality", name: "locality" },
    ].filter((field) =>
      _get(latestOfficial.residentialAddressDetails, field.name)
    );

    residentialFields.forEach((field) => {
      const currentValue = _get(
        latestOfficial.residentialAddressDetails,
        field.name,
        "N/A"
      );
      const previousValue = _get(
        previousOfficial.residentialAddressDetails,
        field.name,
        "N/A"
      );
      addToChangedFields(field.name, field.key, currentValue, previousValue);
    });
  }

  return changedFields;
};

function ResidentialDetails({
  official,
  changeTypes,
  isHistory,
  hideResolveAllCheckbox,
  isCurrentUserRow,
  viewRestrictedData,
  latestVersion,
}) {
  const emailAddressFormFields = [
    {
      key: "Email address",
      name: "emailAddress",
      value: _get(official.electronicAddress, "emailAddress", "N/A"),
    },
    {
      key: "Mobile number",
      name: "mobileNumber",
      value: _get(official.electronicAddress, "mobileNumber", "N/A"),
    },
  ];

  const residentialDetailsFormFields = [
    {
      key: "Building name",
      name: "buildingName",
      value: _get(official.residentialAddressDetails, "buildingName", "N/A"),
    },
    {
      key: "House number",
      name: "houseNumber",
      value: _get(official.residentialAddressDetails, "houseNumber", "N/A"),
    },
    {
      key: "Estate",
      name: "estate",
      value: _get(official.residentialAddressDetails, "estate", "N/A"),
    },
    {
      key: "Floor number",
      name: "floorNumber",
      value: _get(official.residentialAddressDetails, "floorNumber", "N/A"),
    },
    {
      key: "Street name",
      name: "streetName",
      value: _get(official.residentialAddressDetails, "streetName", "N/A"),
    },
    {
      key: "Street number",
      name: "streetNumber",
      value: _get(official.residentialAddressDetails, "streetNumber", "N/A"),
    },
  ];

  const city = _get(official.residentialAddressDetails, "city");
  if (city) {
    residentialDetailsFormFields.push({
      key: "City",
      name: "city",
      value: city,
    });
  }

  const country = _get(official.residentialAddressDetails, "country");
  if (country) {
    residentialDetailsFormFields.push({
      key: "Country",
      name: "country",
      value: country,
    });
  }

  const county = _get(official.residentialAddressDetails, "county");
  if (county) {
    residentialDetailsFormFields.push({
      key: "County",
      name: "county",
      value: county,
    });
  }

  const district = _get(official.residentialAddressDetails, "district");
  if (district) {
    residentialDetailsFormFields.push({
      key: "District",
      name: "district",
      value: district,
    });
  }

  const locality = _get(official.residentialAddressDetails, "locality");
  if (locality) {
    residentialDetailsFormFields.push({
      key: "Locality",
      name: "locality",
      value: locality,
    });
  }
  // Get changed fields
  const changedFields = getChangedFields(
    official,
    latestVersion,
    viewRestrictedData,
    isCurrentUserRow
  );

  return (
    <>
      <FormFieldsCard
        fields={getTransformedFormFields({
          idType: official.idType,
          formType: "postalAddress",
          formValues: official.postalAddress,
        })}
        changedFields={changedFields}
        isHistory={isHistory}
        hideResolveAllCheckbox={hideResolveAllCheckbox}
      />
      {(isCurrentUserRow || viewRestrictedData) && (
        <>
          <FormFieldsCard
            fields={emailAddressFormFields}
            changedFields={changedFields}
            isHistory={isHistory}
            hideResolveAllCheckbox={hideResolveAllCheckbox}
          />
          <FormFieldsCard
            fields={residentialDetailsFormFields}
            changedFields={changedFields}
            isHistory={isHistory}
            hideResolveAllCheckbox={hideResolveAllCheckbox}
          />
        </>
      )}
    </>
  );
}

export default ResidentialDetails;
